import type {
  BySelectionCounts,
  ProductClassCounts,
} from '~/features/already-curated/types/already-curated.types';
import type {
  NotEnrichedBucket,
  NotEnrichedSummary,
} from '~/features/not-enriched/types/not-enriched.types';

import type {
  IncludableProductClass,
  ProductClass,
  ProductGroup,
  ProductGroupCategory,
  ProductGroupPart,
  ProductGroupPartId,
  ProductGroupsChoice,
} from '../types/product-groups.types';

/** Label keys of each backend population, under `productGroups.parts`. */
const CLASS_LABEL: Record<ProductClass, string> = {
  new: 'new',
  never_linked: 'neverLinked',
  flagged: 'flagged',
  curated: 'curated',
};

const BUCKET_LABEL: Record<NotEnrichedBucket, string> = {
  DECLINED_AT_COST_GATE: 'declined',
  NOT_REACHED: 'notReached',
  BEYOND_CAP: 'beyondCap',
  FAILED_RETRYABLE: 'failedRetryable',
  COULD_NOT_SAVE: 'couldNotSave',
  FAILED_PERMANENT: 'failedPermanent',
  EXCLUDED: 'excluded',
};

const BUCKET_COUNT: Record<NotEnrichedBucket, (s: NotEnrichedSummary) => number | undefined> = {
  DECLINED_AT_COST_GATE: (s) => s.declinedAtCostGate,
  NOT_REACHED: (s) => s.notReached,
  BEYOND_CAP: (s) => s.beyondCap,
  FAILED_RETRYABLE: (s) => s.failedRetryable,
  COULD_NOT_SAVE: (s) => s.couldNotSave,
  FAILED_PERMANENT: (s) => s.failedPermanent,
  EXCLUDED: (s) => s.excluded,
};

interface GroupDef {
  key: string;
  category: ProductGroupCategory;
  labelKey: string;
  color: string;
  parts: ProductGroupPartId[];
  /** Ask before including: a retry alone will not fix these products. */
  confirm?: boolean;
}

const cls = (id: ProductClass): ProductGroupPartId => ({ kind: 'class', id });
const bucket = (id: NotEnrichedBucket): ProductGroupPartId => ({ kind: 'bucket', id });

/**
 * The groups an operator decides on, in display order. Each is one decision: products never
 * enriched (new in the feed, or set up by no run; or curated but never sent to the agent),
 * products an earlier batch failed (retry may fix it, or it will not), and products already in
 * the catalog.
 */
const GROUP_DEFS: GroupDef[] = [
  {
    key: 'new',
    category: 'fresh',
    labelKey: 'newProducts',
    color: '#4B57EA',
    parts: [cls('new'), cls('never_linked')],
  },
  {
    key: 'stopped',
    category: 'fresh',
    labelKey: 'stopped',
    color: '#7C8CF8',
    parts: [bucket('DECLINED_AT_COST_GATE'), bucket('NOT_REACHED'), bucket('BEYOND_CAP')],
  },
  {
    key: 'excluded',
    category: 'fresh',
    labelKey: 'excluded',
    color: '#8D94A3',
    parts: [bucket('EXCLUDED')],
  },
  {
    key: 'retry',
    category: 'failed',
    labelKey: 'canRetry',
    color: '#E5735A',
    parts: [bucket('FAILED_RETRYABLE'), bucket('COULD_NOT_SAVE')],
  },
  {
    key: 'blocked',
    category: 'failed',
    labelKey: 'blocked',
    color: '#A35C5C',
    parts: [bucket('FAILED_PERMANENT')],
    confirm: true,
  },
  {
    key: 'flagged',
    category: 'catalog',
    labelKey: 'flagged',
    color: '#E8B21B',
    parts: [cls('flagged')],
  },
  {
    key: 'curated',
    category: 'catalog',
    labelKey: 'curated',
    color: '#8A93A6',
    parts: [cls('curated')],
  },
];

/** The tree's branches, in display order. */
export const PRODUCT_GROUP_CATEGORIES: ProductGroupCategory[] = ['fresh', 'failed', 'catalog'];

/** A part's label key under `productGroups.parts`. */
export function partLabelKey(part: ProductGroupPartId): string {
  return part.kind === 'class' ? CLASS_LABEL[part.id] : BUCKET_LABEL[part.id];
}

function partCount(
  part: ProductGroupPartId,
  classes: ProductClassCounts | null | undefined,
  notEnriched: NotEnrichedSummary | null | undefined,
): number | null {
  if (part.kind === 'class') return classes ? classes[part.id] : null;
  return notEnriched ? (BUCKET_COUNT[part.id](notEnriched) ?? 0) : null;
}

/** Every group the create screen offers, with its parts' counts for the scope. */
export function buildProductGroups(
  classes: ProductClassCounts | null | undefined,
  notEnriched: NotEnrichedSummary | null | undefined,
): ProductGroup[] {
  return GROUP_DEFS.map((d) => {
    const parts: ProductGroupPart[] = d.parts.map((part) => ({
      part,
      labelKey: partLabelKey(part),
      count: partCount(part, classes, notEnriched),
    }));
    const known = parts.every((p) => p.count !== null);
    return {
      key: d.key,
      category: d.category,
      labelKey: d.labelKey,
      color: d.color,
      parts,
      count: known ? parts.reduce((n, p) => n + (p.count ?? 0), 0) : null,
      includable: true,
      confirmBeforeInclude: d.confirm === true,
    };
  });
}

/** Whether every part of the group is a curation class (an `all` run takes those). */
export function isSyncGroup(group: ProductGroup): boolean {
  return group.parts.every((p) => p.part.kind === 'class');
}

function hasPart(choice: ProductGroupsChoice, part: ProductGroupPartId): boolean {
  if (part.kind !== 'class') return choice.buckets.includes(part.id);
  // An `all` run (already-curated included) takes every class of the sync.
  return choice.allSync === true || (choice.includeClasses as string[]).includes(part.id);
}

/** Whether the group is the already-curated one: including it makes the run an `all` run. */
export function isCuratedGroup(group: ProductGroup): boolean {
  return group.parts.some((p) => p.part.kind === 'class' && p.part.id === 'curated');
}

/** Under an `all` run, a sync group other than already-curated is taken and cannot be left out. */
export function isLockedIn(choice: ProductGroupsChoice | null, group: ProductGroup): boolean {
  return choice?.allSync === true && isSyncGroup(group) && !isCuratedGroup(group);
}

/** Whether `choice` includes `group` (every part of it). */
export function isIncluded(choice: ProductGroupsChoice | null, group: ProductGroup): boolean {
  if (!choice) return false;
  return group.parts.every((p) => hasPart(choice, p.part));
}

/** `choice` with every part of `group` included or left out. */
export function withIncluded(
  choice: ProductGroupsChoice,
  group: ProductGroup,
  include: boolean,
): ProductGroupsChoice {
  if (!group.includable) return choice;
  if (isCuratedGroup(group)) return { ...choice, allSync: include };
  if (isLockedIn(choice, group)) return choice;
  let { includeClasses, buckets } = choice;
  for (const { part } of group.parts) {
    if (part.kind === 'class') {
      const id = part.id as IncludableProductClass;
      includeClasses = includeClasses.filter((c) => c !== id);
      if (include) includeClasses = [...includeClasses, id];
    } else {
      const id = part.id;
      buckets = buckets.filter((b) => b !== id);
      if (include) buckets = [...buckets, id];
    }
  }
  return { ...choice, includeClasses, buckets };
}

/** Products the choice includes, over the groups whose counts are known. */
export function includedTotal(groups: ProductGroup[], choice: ProductGroupsChoice | null): number {
  return groups.reduce((n, g) => n + (isIncluded(choice, g) ? (g.count ?? 0) : 0), 0);
}

/** The class counts to keep on the batch (`productClassCounts`), from curation's split. */
export function classCountsOf(classes: ProductClassCounts | null | undefined) {
  if (!classes) return null;
  const out: Record<string, number> = {};
  for (const id of PRODUCT_CLASS_ORDER) {
    const n = classes[id];
    if (typeof n === 'number') out[id] = n;
  }
  return out;
}

/**
 * The class split the create screen works from, whatever curation sent. A curation from before
 * `changed` was retired sends `changed` / `unchanged` instead of `curated` (both are already in the
 * catalog); one from before classes existed sends only the 3-bucket breakdown, whose net-new
 * count stands in for new (never-finished products are counted with the curated ones there).
 * Null only while there is no breakdown at all, so the products step never waits on a field an
 * older curation will never send.
 */
export function classCountsFrom(
  counts: BySelectionCounts | null | undefined,
): ProductClassCounts | null {
  if (!counts) return null;
  const c = counts.classes as (Partial<Record<string, number>> & object) | null | undefined;
  if (c) {
    return {
      new: c.new ?? 0,
      never_linked: c.never_linked ?? 0,
      flagged: c.flagged ?? 0,
      curated: c.curated ?? (c.unchanged ?? 0) + (c.changed ?? 0),
    };
  }
  return {
    new: counts.net_new,
    never_linked: 0,
    flagged: counts.already_curated_flagged,
    curated: counts.already_curated_clean,
  };
}

/** A fresh, nothing-included choice for a subsidiary. */
export function emptyChoice(subsidiaryId: string): ProductGroupsChoice {
  return { subsidiaryId, includeClasses: [], buckets: [], classCounts: null };
}

/** Curation classes in display order. */
export const PRODUCT_CLASS_ORDER: ProductClass[] = ['new', 'never_linked', 'flagged', 'curated'];

/** A class's label key (under `productGroups.parts`) and its group's colour. */
export function classDef(id: string): { labelKey: string; color: string } | null {
  if (!(id in CLASS_LABEL)) return null;
  const klass = id as ProductClass;
  const group = GROUP_DEFS.find((g) => g.parts.some((p) => p.kind === 'class' && p.id === klass));
  return { labelKey: CLASS_LABEL[klass], color: group?.color ?? '#8A93A6' };
}

/** A finite number, else null (the class counts arrive as untyped JSON). */
const countOf = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * The batch's product total as the breakdown's Total row reads it: every counted class, plus
 * carried-in and the adjustment. Null until curation has counted any class ("—", never 0).
 */
export function productClassesTotal(
  included: Record<string, unknown> | null | undefined,
  carriedIn: number | null = null,
  adjustment: number | null = null,
): number | null {
  const counts = PRODUCT_CLASS_ORDER.map((id) => countOf(included?.[id])).filter(
    (c): c is number => c !== null,
  );
  if (counts.length === 0) return null;
  return counts.reduce((n, c) => n + c, 0) + (countOf(carriedIn) ?? 0) + (countOf(adjustment) ?? 0);
}

/**
 * The class fields a batch request carries for the included groups: the classes only under
 * net_new (an `all` run takes every class), the counts the stepper showed when it has them.
 * Shared by the create request and the full-catalog trigger so the two never disagree.
 */
export function productClassFields(
  choice: Pick<ProductGroupsChoice, 'includeClasses' | 'classCounts'>,
  ingestMode: string | null | undefined,
): { includeClasses?: IncludableProductClass[]; productClassCounts?: Record<string, number> } {
  return {
    ...(ingestMode !== 'all' ? { includeClasses: choice.includeClasses } : {}),
    ...(choice.classCounts ? { productClassCounts: choice.classCounts } : {}),
  };
}
